import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import z from "zod";

import { getDatasourceFileUrl } from "@/core/lib/api";
import { fetchDatasourcesByIds } from "@/core/lib/datasources-by-ids";
import { coerceToStringArray } from "@/core/lib/search-params";
import {
  getDocumentAnalysisTemplateOptions,
  getDocumentAnalysisTemplatesOptions,
} from "@/openapi/@tanstack/react-query.gen";

const searchSchema = z.object({
  templateId: z.uuid(),
  datasourceId: z.uuid(),
  runNumber: z.coerce.number().int().positive().optional(),
  selectedConditions: z
    .preprocess(coerceToStringArray, z.array(z.string()))
    .optional(),
  promotionRequestId: z.uuid().optional(),
});

export const Route = createFileRoute("/_protected/document-analysis/results")({
  validateSearch: searchSchema,
  loaderDeps: ({
    search: { templateId, datasourceId, promotionRequestId },
  }) => ({
    templateId,
    datasourceId,
    promotionRequestId,
  }),
  loader: async ({ context: { queryClient, ...context }, deps }) => {
    const orgId = context.user.organization?.id;

    if (!orgId) {
      throw redirect({ to: "/no-organization", throw: true });
    }

    const [datasourcesData, templateData] = await Promise.all([
      fetchDatasourcesByIds(queryClient, orgId, [deps.datasourceId]),
      deps.promotionRequestId
        ? queryClient.query(
            getDocumentAnalysisTemplateOptions({
              path: { org_id: orgId, template_id: deps.templateId },
              query: { promotion_request_id: deps.promotionRequestId },
            }),
          )
        : queryClient.query(
            getDocumentAnalysisTemplatesOptions({ path: { org_id: orgId } }),
          ),
    ]);

    const datasource = datasourcesData.find((d) => d.id === deps.datasourceId);
    const template = Array.isArray(templateData)
      ? templateData.find((item) => item.id === deps.templateId)
      : templateData;
    const pdfUrl = getDatasourceFileUrl(datasource);
    if (!datasource || !pdfUrl || !template) {
      throw notFound();
    }

    return {
      datasource,
      template,
      pdfUrl,
    };
  },
});
